// Analytics hooks for dashboard charts
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { formatDateLocal, normalizeDateToMidnight } from '@/lib/utils'
import { isAssistantLoggedIn } from '@/lib/permissions'

export interface ServerOrderStats {
  serverId: string
  serverName: string
  serverType: 'staff' | 'admin'
  orderCount: number
  totalRevenue: number
}

export interface MenuItemPopularity {
  menuItemId: string
  menuItemNameAr: string
  menuItemNameFr: string
  orderCount: number
  quantitySold: number
  totalRevenue: number
}

/**
 * Fetch server order statistics for a date range
 */
export function useServerOrderStats(
  restaurantId: string | null,
  startDate: Date,
  endDate: Date
) {
  const normalizedStart = normalizeDateToMidnight(startDate)
  const startDateString = formatDateLocal(normalizedStart)
  const endDateString = formatDateLocal(endDate)

  return useQuery({
    queryKey: ['chartAnalytics', 'serverOrders', restaurantId, startDateString, endDateString],
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<ServerOrderStats[]> => {
      if (!restaurantId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await (supabase.rpc as any)('assistant_get_server_order_stats', {
          p_restaurant_id: restaurantId,
          p_start_date: startDateString,
          p_end_date: endDateString,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have access to view dashboard')
          }
          throw error
        }

        return (data || []).map((item: any) => ({
          serverId: item.server_id,
          serverName: item.server_name,
          serverType: item.server_type as 'staff' | 'admin',
          orderCount: Number(item.order_count) || 0,
          totalRevenue: Number(item.total_revenue) || 0,
        }))
      }

      // Admin user - use direct RPC
      const { data, error } = await (supabase.rpc as any)('get_server_order_stats', {
        p_restaurant_id: restaurantId,
        p_start_date: startDateString,
        p_end_date: endDateString,
      })

      if (error) {
        throw error
      }

      return (data || []).map((item: any) => ({
        serverId: item.server_id,
        serverName: item.server_name,
        serverType: item.server_type as 'staff' | 'admin',
        orderCount: Number(item.order_count) || 0,
        totalRevenue: Number(item.total_revenue) || 0,
      }))
    },
    enabled: !!restaurantId,
  })
}

/**
 * Fetch menu item popularity statistics for a date range
 */
export function useMenuItemPopularity(
  restaurantId: string | null,
  startDate: Date,
  endDate: Date
) {
  const normalizedStart = normalizeDateToMidnight(startDate)
  const startDateString = formatDateLocal(normalizedStart)
  const endDateString = formatDateLocal(endDate)

  return useQuery({
    queryKey: ['chartAnalytics', 'menuPopularity', restaurantId, startDateString, endDateString],
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<MenuItemPopularity[]> => {
      if (!restaurantId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await (supabase.rpc as any)('assistant_get_menu_item_popularity', {
          p_restaurant_id: restaurantId,
          p_start_date: startDateString,
          p_end_date: endDateString,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have access to view dashboard')
          }
          throw error
        }

        return (data || []).map((item: any) => ({
          menuItemId: item.menu_item_id,
          menuItemNameAr: item.menu_item_name_ar,
          menuItemNameFr: item.menu_item_name_fr,
          orderCount: Number(item.order_count) || 0,
          quantitySold: Number(item.quantity_sold) || 0,
          totalRevenue: Number(item.total_revenue) || 0,
        }))
      }

      // Admin user - use direct RPC
      const { data, error } = await (supabase.rpc as any)('get_menu_item_popularity', {
        p_restaurant_id: restaurantId,
        p_start_date: startDateString,
        p_end_date: endDateString,
      })

      if (error) {
        throw error
      }

      return (data || []).map((item: any) => ({
        menuItemId: item.menu_item_id,
        menuItemNameAr: item.menu_item_name_ar,
        menuItemNameFr: item.menu_item_name_fr,
        orderCount: Number(item.order_count) || 0,
        quantitySold: Number(item.quantity_sold) || 0,
        totalRevenue: Number(item.total_revenue) || 0,
      }))
    },
    enabled: !!restaurantId,
  })
}
